import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import type { TurnInputRequest, SubmitInputResponseArgs } from '../../../hooks/useWorkspace';

/* ---------- styled ---------- */

const Card = styled.section<{ $answered: boolean }>`
  border: 1px solid
    ${({ $answered }) =>
      $answered ? 'var(--border)' : 'var(--accent, #6366f1)'};
  border-radius: 12px;
  background: ${({ $answered }) =>
    $answered ? 'var(--bg-elevated)' : 'var(--bg-surface)'};
  box-shadow: ${({ $answered }) =>
    $answered
      ? 'none'
      : '0 4px 12px rgba(99, 102, 241, 0.08)'};
  padding: 0.875rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
`;

const HeaderLabel = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.6875rem;
  font-weight: 700;
  color: var(--accent, #6366f1);
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;

const HeaderDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--accent, #6366f1);
`;

const AgentTag = styled.span`
  font-size: 0.75rem;
  color: var(--text-muted);
`;

const Prompt = styled.div`
  font-size: 0.9375rem;
  line-height: 1.55;
  color: var(--text-primary);
  white-space: pre-wrap;
`;

const OptionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
`;

const OptionRow = styled.label<{ $selected: boolean; $disabled: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 0.625rem;
  padding: 0.5rem 0.75rem;
  border-radius: 8px;
  border: 1px solid
    ${({ $selected }) =>
      $selected ? 'var(--accent, #6366f1)' : 'var(--border)'};
  background: ${({ $selected }) =>
    $selected ? 'var(--accent-subtle, rgba(99,102,241,0.06))' : 'transparent'};
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
  transition: border-color 0.15s, background 0.15s;

  &:hover {
    border-color: ${({ $disabled }) =>
      $disabled ? undefined : 'var(--accent, #6366f1)'};
  }
`;

const Shortcut = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 4px;
  background: var(--bg-elevated);
  border: 1px solid var(--border);
  font-size: 0.6875rem;
  font-family: var(--mono, ui-monospace, monospace);
  color: var(--text-muted);
  flex-shrink: 0;
  margin-top: 0.125rem;
`;

const OptionContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const OptionLabel = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-primary);
`;

const OptionDescription = styled.div`
  font-size: 0.75rem;
  color: var(--text-muted);
  margin-top: 0.125rem;
  line-height: 1.4;
`;

const RadioOrCheck = styled.input`
  flex-shrink: 0;
  margin-top: 0.25rem;
`;

const FreeTextToggle = styled.button`
  align-self: flex-start;
  padding: 0.25rem 0.5rem;
  border: none;
  background: transparent;
  color: var(--accent, #6366f1);
  font-family: inherit;
  font-size: 0.75rem;
  cursor: pointer;
  border-radius: 6px;

  &:hover {
    background: var(--accent-subtle, rgba(99, 102, 241, 0.08));
  }
`;

const FreeTextArea = styled.textarea`
  width: 100%;
  min-height: 72px;
  padding: 0.5rem 0.625rem;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--input-bg);
  font-family: inherit;
  font-size: 0.875rem;
  color: var(--text-primary);
  outline: none;
  resize: vertical;

  &:focus {
    border-color: var(--accent, #6366f1);
  }
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
`;

const PrimaryBtn = styled.button`
  padding: 0.375rem 0.875rem;
  border-radius: 8px;
  border: none;
  background: var(--accent, #6366f1);
  color: white;
  font-family: inherit;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    background: var(--accent-hover, #4f46e5);
  }
`;

const SecondaryBtn = styled.button`
  padding: 0.375rem 0.75rem;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: var(--bg-surface);
  color: var(--text-secondary);
  font-family: inherit;
  font-size: 0.8125rem;
  cursor: pointer;

  &:hover {
    background: var(--bg-elevated);
  }
`;

const AnsweredRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  font-size: 0.8125rem;
  color: var(--text-secondary);
`;

const AnsweredText = styled.span`
  color: var(--text-primary);
  font-weight: 500;
`;

/* ---------- component ---------- */

interface AgentInputRequestCardProps {
  inputRequest: TurnInputRequest;
  isPending: boolean;
  onSubmit: (args: SubmitInputResponseArgs) => void;
}

function humanReadable(
  inputType: string,
  selected: string[],
  options: { id: string; label: string }[],
  freeText: string,
): string {
  if (inputType === 'free_text') return freeText.trim();
  const labels = selected
    .map((id) => options.find((o) => o.id === id)?.label ?? id)
    .filter(Boolean);
  const joined = labels.join(', ');
  if (freeText.trim()) {
    return joined ? `${joined} — ${freeText.trim()}` : freeText.trim();
  }
  return joined;
}

/**
 * Renders an inline card for a pending {@link TurnInputRequest}. Supports
 * the three input types emitted by the WMA: `single_choice`, `multi_choice`
 * and `free_text`. When `allow_free_text` is set on a choice-based request,
 * the user can toggle into a free-text fallback ("Answer in your own words").
 *
 * Keyboard shortcuts 1–9 select options (single_choice only) for fast
 * answers. Once submitted, the card collapses to an answered summary with
 * the chosen value(s). Edit support is intentionally out of scope for the
 * first iteration — the plan still shows the answer below a subtle Edit
 * affordance for a future follow-up.
 */
const AgentInputRequestCard: React.FC<AgentInputRequestCardProps> = ({
  inputRequest,
  isPending,
  onSubmit,
}) => {
  const { prompt, inputSchema, requestingAgent, status, humanReadableAnswer } =
    inputRequest;
  const inputType = inputSchema.inputType;
  const options = inputSchema.options ?? [];
  const allowFreeText = Boolean(inputSchema.allowFreeText);
  const minSelections = inputSchema.minSelections ?? 1;

  const [selected, setSelected] = useState<string[]>([]);
  const [freeTextMode, setFreeTextMode] = useState(inputType === 'free_text');
  const [freeText, setFreeText] = useState('');

  useEffect(() => {
    setFreeTextMode(inputType === 'free_text');
  }, [inputType]);

  const disabled = !isPending || status !== 'awaiting';

  const handleOptionToggle = useCallback(
    (optionId: string) => {
      if (disabled) return;
      if (inputType === 'single_choice') {
        setSelected([optionId]);
      } else if (inputType === 'multi_choice') {
        setSelected((prev) =>
          prev.includes(optionId)
            ? prev.filter((id) => id !== optionId)
            : [...prev, optionId],
        );
      }
    },
    [disabled, inputType],
  );

  // Keyboard shortcuts (single_choice only, 1..9 picks option).
  useEffect(() => {
    if (disabled) return;
    if (inputType !== 'single_choice') return;
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) {
        return;
      }
      const n = parseInt(e.key, 10);
      if (!Number.isNaN(n) && n >= 1 && n <= 9) {
        const opt = options[n - 1];
        if (opt) {
          e.preventDefault();
          setSelected([opt.id]);
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [disabled, inputType, options]);

  const canSubmit = useMemo(() => {
    if (disabled) return false;
    if (freeTextMode || inputType === 'free_text') {
      return freeText.trim().length > 0;
    }
    if (inputType === 'single_choice') return selected.length === 1;
    if (inputType === 'multi_choice') return selected.length >= minSelections;
    return selected.length > 0;
  }, [disabled, freeTextMode, inputType, freeText, selected, minSelections]);

  const handleSubmit = useCallback(() => {
    if (!canSubmit) return;
    const isFreeText = freeTextMode || inputType === 'free_text';
    const answer = isFreeText
      ? freeText.trim()
      : inputType === 'single_choice'
        ? selected[0]
        : selected;
    onSubmit({
      answer,
      humanReadableAnswer: humanReadable(
        isFreeText ? 'free_text' : inputType,
        selected,
        options,
        freeText,
      ),
    });
  }, [canSubmit, freeTextMode, inputType, freeText, selected, options, onSubmit]);

  if (status === 'answered') {
    return (
      <Card $answered>
        <Header>
          <HeaderLabel>
            <HeaderDot />
            Answered
          </HeaderLabel>
          {requestingAgent && <AgentTag>{requestingAgent}</AgentTag>}
        </Header>
        <AnsweredRow>
          <span>{prompt}</span>
          <AnsweredText>{humanReadableAnswer ?? '—'}</AnsweredText>
        </AnsweredRow>
      </Card>
    );
  }

  const isFreeText = freeTextMode || inputType === 'free_text';

  return (
    <Card
      $answered={false}
      role="form"
      aria-label="Agent input request"
      aria-busy={!isPending}
    >
      <Header>
        <HeaderLabel>
          <HeaderDot />
          Agent needs input
        </HeaderLabel>
        {requestingAgent && <AgentTag>{requestingAgent}</AgentTag>}
      </Header>
      <Prompt>{prompt}</Prompt>

      {!isFreeText && options.length > 0 && (
        <OptionList role={inputType === 'multi_choice' ? 'group' : 'radiogroup'}>
          {options.map((opt, i) => {
            const checked = selected.includes(opt.id);
            return (
              <OptionRow
                key={opt.id}
                $selected={checked}
                $disabled={disabled}
                htmlFor={`opt-${opt.id}`}
              >
                <Shortcut aria-hidden="true">{i < 9 ? i + 1 : '·'}</Shortcut>
                <RadioOrCheck
                  id={`opt-${opt.id}`}
                  type={inputType === 'multi_choice' ? 'checkbox' : 'radio'}
                  name="agent-input-request"
                  checked={checked}
                  disabled={disabled}
                  onChange={() => handleOptionToggle(opt.id)}
                />
                <OptionContent>
                  <OptionLabel>{opt.label}</OptionLabel>
                  {opt.description && (
                    <OptionDescription>{opt.description}</OptionDescription>
                  )}
                </OptionContent>
              </OptionRow>
            );
          })}
        </OptionList>
      )}

      {isFreeText && (
        <FreeTextArea
          placeholder={
            inputType === 'free_text'
              ? 'Type your answer...'
              : 'Answer in your own words...'
          }
          value={freeText}
          onChange={(e) => setFreeText(e.target.value)}
          disabled={disabled}
          aria-label="Free text answer"
        />
      )}

      {allowFreeText && inputType !== 'free_text' && !freeTextMode && (
        <FreeTextToggle type="button" onClick={() => setFreeTextMode(true)}>
          Answer in your own words instead
        </FreeTextToggle>
      )}

      <Actions>
        {freeTextMode && inputType !== 'free_text' && (
          <SecondaryBtn
            type="button"
            onClick={() => {
              setFreeTextMode(false);
              setFreeText('');
            }}
          >
            Back to options
          </SecondaryBtn>
        )}
        <PrimaryBtn type="button" onClick={handleSubmit} disabled={!canSubmit}>
          Submit
        </PrimaryBtn>
      </Actions>
    </Card>
  );
};

export default AgentInputRequestCard;
